import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Row, Spinner, Stack, Table } from "react-bootstrap";
import {
  CATEGORIES,
  RECOMMENDATIONS,
  getCategory,
  weightedOverall,
  type Aggregate,
  type Candidate,
  type Scorecard,
} from "@ld-scorecard/shared";
import { useApi, ApiError } from "../api";
import { useMe } from "../App";
import StatCard from "../components/StatCard";
import { CategoryRadar, RecommendationDonut, SpreadBar } from "../components/charts";
import { useFlag, FLAGS } from "../flags";

const SHORT: Record<string, string> = {
  intro: "Intro",
  discovery: "Discovery",
  pov: "POV Playback",
  "value-fluency": "Value Fluency",
  "handling-room": "Handling Room",
  "self-reflection": "Self-Reflection",
};

export default function CandidateResultsPage() {
  const { id = "" } = useParams();
  const api = useApi();
  const me = useMe();
  const canExport = useFlag(FLAGS.SCORECARD_CSV_EXPORT);
  const canDeleteScorecard = useFlag(FLAGS.ADMIN_DELETE_SCORECARD);
  const analytics = useFlag(FLAGS.RESULTS_ANALYTICS);
  const radarFlag = useFlag(FLAGS.CHART_CATEGORY_RADAR);
  const donutFlag = useFlag(FLAGS.CHART_RECOMMENDATION_DONUT);
  const spreadFlag = useFlag(FLAGS.CHART_PANELIST_AGREEMENT);
  const showRadar = analytics && radarFlag;
  const showDonut = analytics && donutFlag;
  const showSpread = analytics && spreadFlag;

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [agg, setAgg] = useState<Aggregate | null>(null);
  const [cards, setCards] = useState<Scorecard[]>([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    const [cand, a, list] = await Promise.all([
      api.getCandidate(id),
      api.getAggregate(id),
      api.getScorecards(id),
    ]);
    setCandidate(cand);
    setAgg(a);
    setCards(list);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setGated(true);
        else setError(String(e.message ?? e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  const removeScorecard = async (email: string) => {
    if (!confirm(`Delete ${email}'s scorecard for ${candidate?.name}? This can't be undone.`)) return;
    try {
      await api.deleteScorecard(id, email);
      await reload();
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  const download = async () => {
    const blob = await api.downloadExport(id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${candidate?.name ?? "candidate"}_scorecards.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Spinner animation="border" />;

  if (gated) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Results are locked</Alert.Heading>
        <p>Submit your own scorecard first to see the aggregate — this keeps reviewers independent.</p>
        <Link to={`/candidates/${id}`} className="btn btn-primary">Go to my scorecard</Link>
      </Alert>
    );
  }
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!agg) return null;

  const radarLabels = CATEGORIES.map((c) => SHORT[c.id] ?? c.title);
  const radarSeries = [
    { name: candidate?.name ?? "Candidate", data: CATEGORIES.map((c) => agg.categoryAverages[c.id] ?? 0) },
    { name: "Solid (3.0)", data: CATEGORIES.map(() => 3) },
  ];

  const spreadCats = CATEGORIES.filter((c) => agg.categorySpread[c.id]);
  const spreadLabels = spreadCats.map((c) => SHORT[c.id] ?? c.title);
  const spreadRanges = spreadCats.map((c) => {
    const s = agg.categorySpread[c.id]!;
    return { min: s.min, max: s.max };
  });

  const anyRecs = RECOMMENDATIONS.some((r) => agg.recommendationTally[r.value] > 0);

  return (
    <>
      <div className="mb-2">
        <Link to={candidate ? `/requisitions/${candidate.reqId}` : "/"} className="btn btn-link px-0">
          ← Requisition
        </Link>
      </div>
      <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
        <h1 className="h3 mb-0">{candidate?.name} — Panel results</h1>
        {me?.role === "admin" && canExport && (
          <Button className="ms-auto" size="sm" variant="outline-secondary" onClick={() => void download()}>
            <i className="bi bi-download me-1"></i>Export CSV
          </Button>
        )}
      </Stack>

      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <StatCard
            label="Weighted overall"
            value={agg.weightedOverall === null ? "—" : `${agg.weightedOverall}`}
            hint="out of 5"
            icon="bi-speedometer2"
            tone="primary"
          />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="Scorecards" value={agg.submittedCount} icon="bi-check2-square" tone="info" />
        </Col>
      </Row>

      {(showRadar || showDonut) && (
      <Row className="g-3 mb-3">
        {showRadar && (
        <Col lg={showDonut ? 6 : 12}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">Category profile</Card.Header>
            <Card.Body>
              <CategoryRadar labels={radarLabels} series={radarSeries} />
            </Card.Body>
          </Card>
        </Col>
        )}
        {showDonut && (
        <Col lg={showRadar ? 6 : 12}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">Recommendations</Card.Header>
            <Card.Body>
              {anyRecs ? (
                <RecommendationDonut tally={agg.recommendationTally} />
              ) : (
                <p className="text-muted mb-0">No recommendations recorded yet.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        )}
      </Row>
      )}

      {showSpread && spreadCats.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header className="fw-semibold">
            Panelist agreement <span className="text-muted small">— min–max category score across panelists (wide = disagreement)</span>
          </Card.Header>
          <Card.Body>
            <SpreadBar labels={spreadLabels} ranges={spreadRanges} />
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-sm mb-3">
        <Card.Header className="fw-semibold">Category averages</Card.Header>
        <Table responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Category</th>
              <th className="text-center">Weight</th>
              <th className="text-center">Avg (1–5)</th>
              <th className="text-center">Spread (σ)</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((c) => (
              <tr key={c.id}>
                <td>{c.ordinal}. {c.title}</td>
                <td className="text-center">{c.weight}%</td>
                <td className="text-center fw-semibold">{agg.categoryAverages[c.id] ?? "—"}</td>
                <td className="text-center text-muted">{agg.categorySpread[c.id]?.stdev ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Header className="fw-semibold">Signal averages</Card.Header>
        <Card.Body>
          {CATEGORIES.map((c) => (
            <div key={c.id} className="mb-3">
              <div className="fw-semibold mb-1">{c.ordinal}. {c.title}</div>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  {c.signals.map((s) => (
                    <tr key={s.id}>
                      <td>{s.text}</td>
                      <td className="text-end fw-semibold" style={{ width: 60 }}>
                        {agg.signalAverages[c.id]?.[s.id] ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </Card.Body>
      </Card>

      {Object.keys(agg.redFlagTally).length > 0 && (
        <Card className="shadow-sm mb-3 border-danger">
          <Card.Header className="fw-semibold text-danger">Red flags observed</Card.Header>
          <Card.Body>
            {Object.entries(agg.redFlagTally).map(([catId, flags]) => {
              const cat = getCategory(catId);
              const seen = Object.entries(flags).filter(([, count]) => count > 0);
              if (seen.length === 0) return null;
              return (
                <div key={catId} className="mb-2">
                  <div className="small text-muted">{cat?.title}</div>
                  {seen.map(([flagId, count]) => {
                    const flag = cat?.redFlags?.find((f) => f.id === flagId);
                    return (
                      <div key={flagId}>
                        <Badge bg="danger" className="me-2">{count}</Badge>
                        {flag?.text ?? flagId}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      {cards.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header className="fw-semibold">Submitted scorecards</Card.Header>
          <Table responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Panelist</th>
                <th className="text-center">Weighted overall</th>
                <th>Recommendation</th>
                {me?.role === "admin" && canDeleteScorecard && <th className="text-end">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {cards.map((sc) => {
                const wo = weightedOverall(sc);
                const rec = RECOMMENDATIONS.find((r) => r.value === sc.recommendation);
                return (
                  <tr key={sc.email}>
                    <td>
                      <div className="fw-semibold">{sc.panelistName ?? sc.email}</div>
                      {sc.panelistName && <div className="text-muted small">{sc.email}</div>}
                    </td>
                    <td className="text-center">{wo === null ? "—" : `${wo} / 5`}</td>
                    <td>{rec?.label ?? "—"}</td>
                    {me?.role === "admin" && canDeleteScorecard && (
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => void removeScorecard(sc.email)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <FreeTextCard title="Coach every SE to do this" items={agg.coachEveryone} />
      <FreeTextCard title="Gaps of concern (first 90 days)" items={agg.gapConcern} />
    </>
  );
}

function FreeTextCard({ title, items }: { title: string; items: { email: string; text: string }[] }) {
  if (items.length === 0) return null;
  return (
    <Card className="shadow-sm mb-3">
      <Card.Header className="fw-semibold">{title}</Card.Header>
      <Card.Body>
        {items.map((it, i) => (
          <blockquote key={i} className="mb-2">
            <div>“{it.text}”</div>
            <footer className="blockquote-footer">{it.email}</footer>
          </blockquote>
        ))}
      </Card.Body>
    </Card>
  );
}
